<script lang="ts">
  // Push-Approval-Banner. Auf Gerat A im Layout permanent gemounted,
  // haengt sich an /v2-api/auth/push/pending-stream und zeigt eine
  // Banner-Karte sobald ein Pending-Request da ist. Eingabe + WebAuthn
  // Re-Auth laufen ueber die pushAuth-Helpers.
  //
  // Multi-Tab-Koordination: per BroadcastChannel teilen wir mit anderen
  // Tabs des selben Browsers, sobald wir einen Request final
  // abgearbeitet haben (approve/reject/abbrechen). Andere Tabs
  // schliessen ihr Banner dann automatisch.

  import { onMount } from "svelte";

  import {
    approvePushRequest,
    formatPushCode,
    PUSH_CODE_DIGITS,
    rejectPushRequest,
  } from "./pushAuth.svelte";
  import { formatBrowser } from "./devices.svelte";
  import { APIError } from "./api";

  interface PendingRequest {
    id: number;
    ip: string;
    user_agent: string;
    origin: string;
    expires_at: string;
  }

  let pending = $state<PendingRequest | null>(null);
  let code = $state("");
  let busy = $state(false);
  let error = $state<string | null>(null);
  let remaining = $state(0);
  let countdownHandle: ReturnType<typeof setInterval> | null = null;
  let bc: BroadcastChannel | null = null;

  function startCountdown(expiresAt: string) {
    if (countdownHandle) clearInterval(countdownHandle);
    const expires = new Date(expiresAt).getTime();
    const tick = () => {
      remaining = Math.max(0, Math.round((expires - Date.now()) / 1000));
      if (remaining <= 0) {
        // abgelaufen -> Banner schliessen
        clearBanner();
      }
    };
    tick();
    countdownHandle = setInterval(tick, 500);
  }

  function clearBanner() {
    pending = null;
    code = "";
    error = null;
    if (countdownHandle) {
      clearInterval(countdownHandle);
      countdownHandle = null;
    }
  }

  function notifyOtherTabs(action: "resolved", id: number) {
    bc?.postMessage({ action, id });
  }

  // Code-Laenge + Formatter kommen zentral aus pushAuth.svelte.ts, damit
  // sie mit auth.PushCodeDigits im Backend gekoppelt bleiben.
  const CODE_LEN = PUSH_CODE_DIGITS;

  function onApprove() {
    if (busy || !pending || code.trim().length !== CODE_LEN) return;
    void (async () => {
      busy = true;
      error = null;
      const id = pending!.id;
      try {
        await approvePushRequest(id, code.trim());
        notifyOtherTabs("resolved", id);
        clearBanner();
      } catch (e) {
        const msg = e instanceof Error ? e.message : String(e);
        // wrong_code, expired oder reject => harte Meldung. Single-shot,
        // kein Retry. Banner schliessen, damit der User nicht weiter
        // tippt.
        error = msg.includes("Sicherheitsgruenden")
          ? "Aus Sicherheitsgruenden abgebrochen, bitte Login neu starten."
          : msg;
        // Bei wrong_code/expired ist der Request final -- Banner zu. Nur
        // DIESE Anfrage: kommt in den 4 s eine neue, bleibt sie stehen.
        if (/wrong_code|expired|nicht mehr offen|Sicherheits/i.test(msg)) {
          notifyOtherTabs("resolved", id);
          setTimeout(() => {
            if (pending?.id === id) clearBanner();
          }, 4000);
        }
      } finally {
        busy = false;
      }
    })();
  }

  // Ablehnen schließt das Banner nur, wenn die Anfrage danach wirklich zu
  // ist. Bis 21.10.2026 räumte `finally` Banner und Fehler in jedem Fall
  // weg: man glaubte, die fremde Anmeldung abgelehnt zu haben, während
  // sie beim Server offen blieb.
  function onReject() {
    if (busy || !pending) return;
    void (async () => {
      busy = true;
      error = null;
      const id = pending!.id;
      try {
        await rejectPushRequest(id);
        notifyOtherTabs("resolved", id);
        clearBanner();
      } catch (e) {
        // Schon entschieden oder abgelaufen (409/404): nichts mehr offen.
        if (e instanceof APIError && (e.status === 404 || e.status === 409)) {
          notifyOtherTabs("resolved", id);
          clearBanner();
          return;
        }
        // Sonst bleibt das Banner mit dem Grund stehen -- nochmal versuchen.
        error = e instanceof APIError ? e.detail : e instanceof Error ? e.message : String(e);
      } finally {
        busy = false;
      }
    })();
  }

  // Der Strom der Anfragen. Nach einem Netzfehler verbindet der Browser
  // selbst neu; eine Fehlerantwort (502/530 vom Proxy während make
  // deploy) schließt ihn aber endgültig -- bis zum Neuladen kamen dann
  // keine Anfragen mehr. Darum hier selbst neu öffnen, mit Backoff.
  let strom: EventSource | null = null;
  let stromTimer: ReturnType<typeof setTimeout> | undefined;
  let stromWarte = 1000;
  let stromAus = false;

  function oeffneStrom() {
    strom?.close();
    const es = new EventSource("/v2-api/auth/push/pending-stream", {
      withCredentials: true,
    });
    strom = es;
    es.onopen = () => {
      stromWarte = 1000;
    };
    es.addEventListener("push_pending", (ev) => {
      try {
        const data = JSON.parse((ev as MessageEvent).data) as PendingRequest;
        pending = data;
        code = "";
        error = null;
        startCountdown(data.expires_at);
      } catch {
        // ignore malformed event
      }
    });
    es.onerror = () => {
      if (stromAus || strom !== es || es.readyState !== EventSource.CLOSED) return;
      clearTimeout(stromTimer);
      stromTimer = setTimeout(oeffneStrom, stromWarte);
      stromWarte = Math.min(stromWarte * 2, 30_000);
    };
  }

  onMount(() => {
    bc = new BroadcastChannel("homelab-push-approval");
    bc.onmessage = (ev) => {
      if (ev.data?.action === "resolved" && ev.data?.id === pending?.id) {
        clearBanner();
      }
    };

    stromAus = false;
    oeffneStrom();

    return () => {
      stromAus = true;
      clearTimeout(stromTimer);
      strom?.close();
      strom = null;
      bc?.close();
      if (countdownHandle) clearInterval(countdownHandle);
    };
  });

  function fmtSec(s: number): string {
    return `${Math.max(0, s)}s`;
  }

  function fmtUA(ua: string): string {
    if (!ua) return "?";
    const br = formatBrowser(ua);
    if (br) return br;
    return ua.length > 32 ? ua.slice(0, 32) + "…" : ua;
  }
</script>

{#if pending}
  <div
    class="fixed inset-x-0 top-0 z-9000 flex justify-center px-3 pt-3"
    role="alertdialog"
    aria-live="assertive"
  >
    <article
      class="border-warning/40 bg-bg/95 grid w-full max-w-md gap-3 rounded-xl border p-4 shadow-2xl backdrop-blur"
    >
      <header class="flex items-baseline justify-between gap-3">
        <h2 class="text-fg text-sm font-semibold">Login-Anfrage genehmigen?</h2>
        <span class="text-fg-subtle font-mono text-xs tabular-nums">
          {fmtSec(remaining)}
        </span>
      </header>

      <dl class="text-fg-muted grid grid-cols-[80px_1fr] gap-x-3 gap-y-1 text-xs">
        <dt>IP</dt>
        <dd class="text-fg font-mono">{pending.ip || "?"}</dd>
        <dt>Browser</dt>
        <dd class="text-fg">{fmtUA(pending.user_agent)}</dd>
        {#if pending.origin}
          <dt>Origin</dt>
          <dd class="text-fg break-all">{pending.origin}</dd>
        {/if}
      </dl>

      <p class="text-fg-subtle text-xs">
        Auf dem anderen Geraet wird ein {PUSH_CODE_DIGITS}-stelliger Code angezeigt.
        Erst Bio/PIN-Tap, dann Code eintippen.
      </p>

      <input
        type="text"
        inputmode="numeric"
        pattern="[0-9 ]*"
        maxlength={CODE_LEN + 1}
        autocomplete="one-time-code"
        spellcheck="false"
        value={formatPushCode(code)}
        oninput={(e) => {
          const raw = (e.currentTarget as HTMLInputElement).value
            .replace(/\D+/g, "")
            .slice(0, CODE_LEN);
          code = raw;
        }}
        placeholder="—"
        name="otp"
        data-1p-ignore="true"
        data-lpignore="true"
        data-bwignore="true"
        class="border-border text-fg bg-bg/50 mx-auto w-56 rounded-md border px-3 py-2 text-center font-mono text-2xl tracking-widest"
      />

      {#if error}
        <div
          class="text-danger t-meta"
        >
          {error}
        </div>
      {/if}

      <div class="flex items-center justify-between gap-2">
        <button
          type="button"
          class="text-danger text-xs underline-offset-2 hover:underline disabled:opacity-50"
          disabled={busy}
          onclick={onReject}
        >
          Ich war das nicht — ablehnen
        </button>
        <div class="flex gap-2">
          <button
            type="button"
            class="text-fg-muted hover:text-fg text-xs underline-offset-2 hover:underline disabled:opacity-50"
            disabled={busy}
            onclick={clearBanner}
          >
            spaeter
          </button>
          <button
            type="button"
            class="bg-accent/15 text-accent border border-accent/30 rounded-md ctrl-m text-xs font-medium transition disabled:opacity-50"
            disabled={busy || code.trim().length !== CODE_LEN}
            onclick={onApprove}
          >
            {busy ? "läuft…" : "Genehmigen"}
          </button>
        </div>
      </div>
    </article>
  </div>
{/if}
